import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Building2, Save } from "lucide-react";

const blank = () => ({
  name: "", tagline: "", address: "", tel: "", email: "", web: "",
  legal_name: "", pin: "", vat_no: "", vat_rate: "16",
  mpesa_paybill: "", remittance_email: "", bank_details: "", notes: "", footer: "",
});

export default function CompanySettings() {
  const [f, setF] = useState(blank());
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/v1/admin/settings/company")
      .then(({ data }) => {
        const d = data.data || {};
        setF({
          name: d.name || "", tagline: d.tagline || "", address: d.address || "",
          tel: d.tel || "", email: d.email || "", web: d.web || "",
          legal_name: d.legal_name || "", pin: d.pin || "", vat_no: d.vat_no || "",
          vat_rate: d.vat_rate != null ? String(Math.round(Number(d.vat_rate) * 100)) : "16",
          mpesa_paybill: d.mpesa_paybill || "", remittance_email: d.remittance_email || "",
          bank_details: d.bank_details || "",
          notes: Array.isArray(d.notes) ? d.notes.join("\n") : (d.notes || ""),
          footer: d.footer || "",
        });
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load company settings"))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const rate = Math.min(100, Math.max(0, Number(f.vat_rate) || 0)) / 100;
      const payload = { data: {
        name: f.name, tagline: f.tagline, address: f.address, tel: f.tel, email: f.email, web: f.web,
        legal_name: f.legal_name, pin: f.pin, vat_no: f.vat_no, vat_rate: rate,
        mpesa_paybill: f.mpesa_paybill, remittance_email: f.remittance_email, bank_details: f.bank_details,
        notes: f.notes, footer: f.footer,
      }};
      await api.put("/v1/admin/settings/company", payload);
      toast.success("Company details saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const Field = ({ label, k, ph, mono, wide }) => (
    <label className={wide ? "md:col-span-2 block" : "block"}>
      <span className="vtx-eyebrow block mb-1.5">{label}</span>
      <input className={`vtx-input ${mono ? "font-mono" : ""}`} value={f[k]} onChange={set(k)} placeholder={ph} data-testid={`co-${k}`} />
    </label>
  );

  return (
    <PortalLayout section="admin">
      <div className="vtx-eyebrow">/ Admin · Settings</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter inline-flex items-center gap-2"><Building2 size={24} /> Company &amp; invoice details</h1>
      <p className="text-sm text-[#475569] mt-1">These appear on every invoice PDF — the letterhead, tax registration, VAT rate, and payment instructions.</p>

      {!loaded ? (
        <div className="vtx-card mt-6 p-6"><div className="vtx-skeleton h-40" /></div>
      ) : (
        <div className="vtx-card p-6 mt-6 space-y-6 max-w-3xl">
          <div>
            <div className="vtx-eyebrow mb-3">Letterhead</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Company name" k="name" ph="VENTRIX COURIER & LOGISTICS" />
              <Field label="Tagline" k="tagline" ph="Premium Warehouse • Transit • Last-Mile Solutions" />
              <Field label="Address" k="address" ph="Nairobi 00100, Kenya" wide />
              <Field label="Telephone" k="tel" ph="+254 796741131" />
              <Field label="Email" k="email" ph="info@ventrixfreights.com" />
              <Field label="Website" k="web" ph="https://ventrixfreights.com/" wide />
            </div>
          </div>

          <div className="border-t border-[#A9C0E0]/40 pt-4">
            <div className="vtx-eyebrow mb-3">Registration &amp; tax</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Legal name (footer)" k="legal_name" ph="Ventrix Courier & Logistics Ltd" />
              <label className="block">
                <span className="vtx-eyebrow block mb-1.5">VAT rate (%)</span>
                <input type="number" min="0" max="100" step="0.1" className="vtx-input" value={f.vat_rate} onChange={set("vat_rate")} data-testid="co-vat_rate" />
              </label>
              <Field label="KRA PIN" k="pin" ph="P0023487412M" mono />
              <Field label="VAT No." k="vat_no" ph="057621004M" mono />
            </div>
            <p className="mt-2 text-xs text-[#475569]">VAT is added on top of each invoice's taxable amount at this rate.</p>
          </div>

          <div className="border-t border-[#A9C0E0]/40 pt-4">
            <div className="vtx-eyebrow mb-3">Payment instructions</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="M-Pesa Paybill / Business No." k="mpesa_paybill" ph="552233" mono />
              <Field label="Remittance email" k="remittance_email" ph="claims@ventrixfreights.com" />
              <label className="md:col-span-2 block">
                <span className="vtx-eyebrow block mb-1.5">Bank details (optional)</span>
                <textarea className="vtx-input" rows={2} value={f.bank_details} onChange={set("bank_details")} placeholder="Bank, account name, account no., SWIFT…" data-testid="co-bank_details" />
              </label>
            </div>
          </div>

          <div className="border-t border-[#A9C0E0]/40 pt-4">
            <div className="vtx-eyebrow mb-3">Important notes (one per line)</div>
            <textarea className="vtx-input" rows={6} value={f.notes} onChange={set("notes")}
              placeholder="All rates are exclusive of VAT unless otherwise stated…" data-testid="co-notes" />
            <label className="block mt-3">
              <span className="vtx-eyebrow block mb-1.5">Extra footer line (optional)</span>
              <input className="vtx-input" value={f.footer} onChange={set("footer")} data-testid="co-footer" />
            </label>
          </div>

          <div className="flex justify-end">
            <button className="vtx-btn-primary inline-flex items-center gap-2" disabled={busy} onClick={save} data-testid="save-company"><Save size={14} /> Save company details</button>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
